/**
 * Places domain — chain/door detail hooks.
 *
 * Hand-written `useQuery` wrappers around the generated retrieve client
 * functions. They preserve the behaviour of the former `api.useGetLodging`
 * family: `enabled` guards on a truthy id, and retries skip a genuine 404.
 * Error toasts are handled by `apiClient` → `baseAPICall`, so no manual
 * `notifyError` is needed here.
 *
 * Query keys mirror the legacy ones (`['lodging', id]` / `['convenience',
 * id]`) — nothing invalidates these keys, so reproducing them keeps cache
 * behaviour identical.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import axios, { type AxiosError } from 'axios'

import type {
  ConvenienceChainProxy,
  LodgingChainProxy,
} from '../../models/google_place_chain'
import type {
  ConvenienceDoorProxy,
  LodgingDoorProxy,
} from '../../models/google_place_door'
import { getConvenience } from '../generated/clients/getConvenience'
import { getConvenienceDoor } from '../generated/clients/getConvenienceDoor'
import { getLodging } from '../generated/clients/getLodging'
import { getLodgingDoor } from '../generated/clients/getLodgingDoor'

/**
 * Retry predicate matching the legacy detail hooks: no retry on a 404
 * (the record is genuinely missing); otherwise retry up to 3 times.
 */
function retryExcept404(
  failureCount: number,
  error: Error | AxiosError
): boolean {
  return axios.isAxiosError(error) && error?.response?.status === 404
    ? false
    : failureCount < 3
}

export function useGetLodging(
  lodgingId?: number
): UseQueryResult<LodgingChainProxy, Error> {
  return useQuery({
    queryKey: ['lodging', lodgingId],
    queryFn: () =>
      getLodging({
        lodging_id: lodgingId as number,
      }) as unknown as Promise<LodgingChainProxy>,
    enabled: !!lodgingId,
    retry: retryExcept404,
  })
}

export function useGetLodgingDoor(
  lodgingDoorId?: number
): UseQueryResult<LodgingDoorProxy, Error> {
  return useQuery({
    queryKey: ['lodging', lodgingDoorId],
    queryFn: () =>
      getLodgingDoor({
        lodging_id: lodgingDoorId as number,
      }) as unknown as Promise<LodgingDoorProxy>,
    enabled: !!lodgingDoorId,
    retry: retryExcept404,
  })
}

export function useGetConvenience(
  convenienceId?: number
): UseQueryResult<ConvenienceChainProxy, Error> {
  return useQuery({
    queryKey: ['convenience', convenienceId],
    queryFn: () =>
      getConvenience({
        cstore_id: convenienceId as number,
      }) as unknown as Promise<ConvenienceChainProxy>,
    enabled: !!convenienceId,
    retry: retryExcept404,
  })
}

export function useGetConvenienceDoor(
  convenienceDoorId?: number
): UseQueryResult<ConvenienceDoorProxy, Error> {
  return useQuery({
    queryKey: ['convenience', convenienceDoorId],
    queryFn: () =>
      getConvenienceDoor({
        cstore_id: convenienceDoorId as number,
      }) as unknown as Promise<ConvenienceDoorProxy>,
    enabled: !!convenienceDoorId,
    retry: retryExcept404,
  })
}
